import type { ReactNode } from 'react';
import type { DataTableColumn, DataTableColumnBase } from '../types';
import { cx } from '../utils';

/**
 * Column presets: factories that return a `DataTableColumn<T, K>`.
 *
 * The architectural rule these obey, and must keep obeying: a preset is sugar
 * that *composes on top of* the column contract. It never adds a prop to
 * `<DataTable>`. The component's surface stays flat - it consumes
 * `DataTableColumn<T, K>[]` and nothing else - so anything a preset can't express falls
 * back to hand-writing `render`, which keeps working unchanged and can be
 * mixed freely with preset-built columns in the same array.
 *
 * Every preset therefore forwards the full `DataTableColumnBase` escape hatches
 * (`width`, `visible`, `align`, `truncate`, `cellClassName`, `headerClassName`,
 * `sortable`/`sortKey`, `getTitle`, `stopClickPropagation`), so reaching for a
 * preset is never a dead end.
 */

/** Placeholder for a null/empty value. */
export const EMPTY_VALUE = '—';

/** The escape hatches every preset accepts, minus the sort discriminant. */
export interface ColumnPresetBase<T> {
    /** Stable identity - React key for the `<col>`, `<th>` and `<td>`. */
    key: string;
    /** Header text. Defaults to `''` (a deliberately blank header). */
    label?: string;
    /** Feeds the generated `<colgroup>`, e.g. `'12%'` or `'96px'`. */
    width: string;
    /** `false` removes the column entirely. */
    visible?: boolean;
    /**
     * Cell/header alignment. `'left'` is the table default and is spelled out
     * here (rather than left to `undefined`) so a caller can opt *out* of a
     * preset that right-aligns by default, e.g. `amountColumn`.
     */
    align?: 'left' | 'right' | 'center';
    /** Tailwind `truncate` on this column's cells only. Presets set a default. */
    truncate?: boolean;
    /** Extra classes (and this table's padding scheme) for the cells. */
    cellClassName?: string;
    /** Extra classes for the `<th>`. */
    headerClassName?: string;
    /**
     * Hover reveal, rendered through the shared Radix Tooltip with the cell
     * as trigger. Presets that clip their content supply one derived from
     * the cell's text; passing your own always wins.
     */
    getTitle?: (row: T) => string | undefined;
    /** Stop click/keydown reaching the row. Interactive presets default this on. */
    stopClickPropagation?: boolean;
}

/**
 * Preset options, carrying the same sortable/`sortKey` discriminated union as
 * `DataTableColumn` itself so a preset-built column can be sorted with no cast.
 */
export type ColumnPresetOptions<T, K extends string = string> =
    | (ColumnPresetBase<T> & { sortable?: false; sortKey?: never })
    | (ColumnPresetBase<T> & { sortable: true; sortKey: K });

/** Preset-supplied defaults. Anything the caller passes overrides these. */
export interface ColumnPresetDefaults<T> {
    align?: 'left' | 'right' | 'center';
    truncate?: boolean;
    stopClickPropagation?: boolean;
    getTitle?: (row: T) => string | undefined;
    /** Prepended to the caller's `cellClassName`, so the caller's wins. */
    cellClassName?: string;
}

/**
 * Assembles a `DataTableColumn<T, K>` from preset options, a renderer and the preset's
 * defaults. Keeping the union re-created here (rather than casting) is what
 * lets `sortKey: K` stay strictly typed all the way through.
 */
export function buildColumn<T, K extends string>(
    options: ColumnPresetOptions<T, K>,
    render: (row: T, index: number) => ReactNode,
    defaults: ColumnPresetDefaults<T> = {}
): DataTableColumn<T, K> {
    const align = options.align ?? defaults.align;
    const base: DataTableColumnBase<T> = {
        key: options.key,
        label: options.label ?? '',
        width: options.width,
        render,
        visible: options.visible,
        truncate: options.truncate ?? defaults.truncate,
        align: align === 'right' || align === 'center' ? align : undefined,
        cellClassName: cx(defaults.cellClassName, options.cellClassName) || undefined,
        headerClassName: options.headerClassName,
        getTitle: options.getTitle ?? defaults.getTitle,
        stopClickPropagation: options.stopClickPropagation ?? defaults.stopClickPropagation
    };
    return options.sortable
        ? { ...base, sortable: true, sortKey: options.sortKey }
        : { ...base, sortable: false };
}

/** `null`/`undefined`/empty-string-safe text extraction. */
export function textOf(value: string | number | null | undefined): string {
    if (value === null || value === undefined) {
        return '';
    }
    return typeof value === 'number' ? String(value) : value;
}
